import { useEffect, useRef, useState } from 'react';

import {
  BOTTOM_GAP,
  DEFAULT_INITIAL_Y,
  FAB_SIZE,
  RIGHT_GAP,
  TOP_GAP
} from './FloatingButton.constants';

const clamp = (n, min, max) => {
  return Math.max(min, Math.min(max, n));
};
const rightSnapX = () => {
  return window.innerWidth - FAB_SIZE - RIGHT_GAP;
};
const maxY = () => {
  return window.innerHeight - FAB_SIZE - BOTTOM_GAP;
};
const clampY = y => {
  return clamp(y, TOP_GAP, maxY());
};
const debounceTimeout = 240;

export const useFloatingButton = ({ initialY = DEFAULT_INITIAL_Y, onClick, storageKey }) => {
  const draggingRef = useRef(false);
  const movedRef = useRef(false);
  const pointerIdRef = useRef(null);
  const startRef = useRef({ px: 0, py: 0, x: 0, y: 0 });
  const snappingTimeoutRef = useRef(null);

  const [snapping, setSnapping] = useState(false);
  const [position, setPosition] = useState(() => {
    const rawSavedY = storageKey ? window.localStorage.getItem(storageKey) : null;
    const savedY = rawSavedY == null ? NaN : Number(rawSavedY);
    const y = Number.isFinite(savedY) ? savedY : initialY;
    return { x: rightSnapX(), y: clampY(y) };
  });

  useEffect(() => {
    const onResize = () => {
      setSnapping(true);
      setPosition(p => ({
        x: rightSnapX(),
        y: clampY(p.y)
      }));
      if (snappingTimeoutRef.current) window.clearTimeout(snappingTimeoutRef.current);
      snappingTimeoutRef.current = window.setTimeout(() => setSnapping(false), debounceTimeout);
    };
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      if (snappingTimeoutRef.current) window.clearTimeout(snappingTimeoutRef.current);
    };
  }, []);

  const handlePointerDown = e => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;

    setSnapping(false);

    draggingRef.current = true;
    movedRef.current = false;
    pointerIdRef.current = e.pointerId;

    e.currentTarget.setPointerCapture(e.pointerId);

    startRef.current = { px: e.clientX, py: e.clientY, x: position.x, y: position.y };
  };

  const handlePointerMove = e => {
    if (!draggingRef.current) return;
    if (pointerIdRef.current !== e.pointerId) return;

    const dy = e.clientY - startRef.current.py;

    if (Math.abs(dy) > 3) movedRef.current = true;

    setPosition(() => ({
      x: rightSnapX(),
      y: clampY(startRef.current.y + dy)
    }));
  };

  const handlePointerUp = e => {
    if (!draggingRef.current) return;
    if (pointerIdRef.current !== e.pointerId) return;

    draggingRef.current = false;
    pointerIdRef.current = null;

    setSnapping(true);

    setPosition(p => {
      const y = clampY(p.y);
      if (storageKey) window.localStorage.setItem(storageKey, String(y));
      return { x: rightSnapX(), y };
    });

    if (snappingTimeoutRef.current) window.clearTimeout(snappingTimeoutRef.current);
    snappingTimeoutRef.current = window.setTimeout(() => setSnapping(false), 240);

    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
  };

  const handleKeyDown = e => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick(e);
    }
  };

  const handleClick = e => {
    if (movedRef.current) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    onClick && onClick(e);
  };

  return {
    handleClick,
    handleKeyDown,
    handlePointerDown,
    handlePointerMove,
    handlePointerUp,
    position,
    snapping
  };
};
